// src/features/carousel/screens/CarouselNewScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, Form } from 'react-bootstrap';
import useTitle from '../../../hooks/useTitle'; // सुनिश्चित करें कि यह पाथ सही है

// अपने Carousel API हुक्स को इंपोर्ट करें
import { useCreateCarouselItemMutation } from '../services/carouselApi';

const CarouselNewScreen = () => {
    useTitle('Nirakar Satgur: Create Carousel Item');

    const [formData, setFormData] = useState({
        title: '',
        mediaType: 'image', // डिफ़ॉल्ट रूप से 'image'
        link: '',
        linkType: 'none', // डिफ़ॉल्ट रूप से 'none'
        isVisible: true, // डिफ़ॉल्ट रूप से true
        order: 0, // डिफ़ॉल्ट रूप से 0
        errors: {}
    });

    const [selectedMediaFile, setSelectedMediaFile] = useState(null); // इमेज या वीडियो फाइल
    const [previewMediaUrl, setPreviewMediaUrl] = useState(null); // प्रीव्यू URL

    const [createCarouselItem, { isLoading, isSuccess, isError, error }] = useCreateCarouselItemMutation();
    const navigate = useNavigate();

    useEffect(() => {
        if (isSuccess) {
            // फ़ॉर्म को रीसेट करें
            setFormData({
                title: '',
                mediaType: 'image',
                link: '',
                linkType: 'none',
                isVisible: true,
                order: 0,
                errors: {}
            });
            setSelectedMediaFile(null);
            setPreviewMediaUrl(null);
            navigate('/dashboard/carousel'); // लिस्ट स्क्रीन पर नेविगेट करें
        }
    }, [isSuccess, navigate]);

    const validate = () => {
        const errors = {};
        if (!formData.title.trim()) errors.title = 'Title is required';
        if (!selectedMediaFile) errors.mediaFile = 'Media file is required';
        return errors;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
            errors: { ...prev.errors, [name]: '' },
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedMediaFile(file);
            setPreviewMediaUrl(URL.createObjectURL(file)); // प्रीव्यू URL बनाएं
            // मीडिया प्रकार को फ़ाइल के mimetype के आधार पर सेट करें
            if (file.type.startsWith('image/')) {
                setFormData((prev) => ({ ...prev, mediaType: 'image' }));
            } else if (file.type.startsWith('video/')) {
                setFormData((prev) => ({ ...prev, mediaType: 'video' }));
            }
            setFormData((prev) => ({ ...prev, errors: { ...prev.errors, mediaFile: '' } }));
        } else {
            setSelectedMediaFile(null);
            setPreviewMediaUrl(null);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setFormData((prev) => ({ ...prev, errors }));
            return;
        }

        const payload = new FormData();
        payload.append('title', formData.title);
        payload.append('mediaType', formData.mediaType);
        payload.append('link', formData.link);
        payload.append('linkType', formData.linkType);
        payload.append('isVisible', formData.isVisible ? 'true' : 'false'); // बूलियन को स्ट्रिंग के रूप में भेजें
        payload.append('order', formData.order.toString()); // नंबर को स्ट्रिंग के रूप में भेजें

        if (selectedMediaFile) {
            payload.append('mediaFile', selectedMediaFile); // 'mediaFile' यह आपके Multer मिडिलवेयर में अपेक्षित नाम है
        }

        try {
            await createCarouselItem(payload);
        } catch (err) {
            console.error("Carousel item creation failed:", err);
        }
    };

    const { title, mediaType, link, linkType, isVisible, order, errors } = formData;
    const errClass = isError ? "errmsg" : "offscreen";

    return (
        <>
            <h2 className="pageHeading">Create Carousel Item</h2>
            {isError && <p className={errClass}>{error?.data?.message || 'Failed to create item.'}</p>}

            <div className='formWrap'>
                <form onSubmit={handleFormSubmit}>

                    <div className='formGroup'>
                        <label>Title:</label>
                        <input type='text' name='title' value={title} onChange={handleInputChange} placeholder='Enter title for carousel item' />
                        {errors.title && <span className="error">{errors.title}</span>}
                    </div>

                    <div className='formGroup'>
                        <label>Media File (Image/Video):</label>
                        <input type='file' accept='image/*,video/*' onChange={handleFileChange} />
                        {errors.mediaFile && <span className="error">{errors.mediaFile}</span>}
                        {previewMediaUrl && (
                            <div style={{ marginTop: '10px' }}>
                                {mediaType === 'image' ? (
                                    <img src={previewMediaUrl} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px' }} />
                                ) : (
                                    <video src={previewMediaUrl} controls style={{ maxWidth: '200px', maxHeight: '200px' }} />
                                )}
                            </div>
                        )}
                    </div>

                    <div className='formGroup'>
                        <label>Link (Optional):</label>
                        <input type='text' name='link' value={link} onChange={handleInputChange} placeholder='e.g. /events/123 or https://example.com' />
                    </div>

                    <div className='formGroup'>
                        <label>Link Type:</label>
                        <Form.Select name="linkType" value={linkType} onChange={handleInputChange}>
                            <option value="none">None</option>
                            <option value="internal">Internal App Screen</option>
                            <option value="external">External Website URL</option>
                        </Form.Select>
                    </div>

                    <div className='formGroup checkbox-group'>
                        <input type='checkbox' id='isVisible' name='isVisible' checked={isVisible} onChange={handleInputChange} />
                        <label htmlFor='isVisible'>Display in App</label>
                    </div>

                    <div className='formGroup'>
                        <label>Order:</label>
                        <input type='number' name='order' value={order} onChange={handleInputChange} min="0" />
                    </div>

                    <div className='submit'>
                        <button type='submit' className='btn btn-primary' disabled={isLoading}>
                            {isLoading ? <Spinner size="sm" animation="border" /> : 'Create Carousel Item'}
                        </button>
                    </div>

                </form>
            </div>
        </>
    );
};

export default CarouselNewScreen;